import { format } from "date-fns";
import { Plus } from "lucide-react";
import { ShiftChip } from "@/components/ShiftChip";
import { ymd } from "@/lib/dates";

const Cell = ({ day, row, shifts, types, off, compact, onAdd, onEdit }) => (
  <div className={`group space-y-1 border-l border-t border-slate-200 p-1 ${compact ? "min-h-[64px]" : "min-h-[88px] p-1.5"} ${off ? "bg-rose-50" : [0, 6].includes(day.getDay()) ? "bg-slate-50/70" : ""}`}
    data-testid={`rota-cell-${row.id || "open"}-${ymd(day)}`}>
    {off && <p className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{compact ? "OFF" : "TIME OFF"}</p>}
    {shifts.map((s) => <ShiftChip key={s.id} shift={s} type={types[s.shift_type_id]} compact={compact} onClick={() => onEdit(s)} testId={`rota-shift-${s.id}`} />)}
    <button onClick={() => onAdd(row, day)} className="no-print flex w-full items-center justify-center rounded-md py-0.5 text-slate-300 opacity-0 transition-opacity hover:bg-indigo-50 hover:text-indigo-600 group-hover:opacity-100" data-testid={`rota-add-${row.id || "open"}-${ymd(day)}`}>
      <Plus className="h-4 w-4" />
    </button>
  </div>
);

const DayHead = ({ d, horizontal }) => {
  const today = ymd(d) === ymd(new Date());
  return (
    <div className={`border-slate-200 p-2 ${horizontal ? "sticky top-0 z-10 border-l text-center" : "sticky left-0 z-10 border-t"} ${today ? "bg-indigo-50" : "bg-white"}`} data-testid={`rota-day-${ymd(d)}`}>
      <p className="text-[11px] font-semibold uppercase text-slate-400">{format(d, "EEE")}{d.getDate() === 1 || !horizontal ? ` · ${format(d, "MMM")}` : ""}</p>
      <p className={`font-display text-lg font-bold ${today ? "text-indigo-600" : ""}`}>{format(d, "d")}</p>
    </div>
  );
};

const StaffHead = ({ row, hours, horizontal }) => (
  <div className={`border-slate-200 p-2.5 ${horizontal ? "sticky left-0 z-10 border-t" : "sticky top-0 z-10 border-l"} ${row.id ? "bg-white" : "bg-amber-50"}`} data-testid={`rota-row-${row.id || "open"}`}>
    <p className="truncate text-sm font-semibold">{row.name}</p>
    <p className="truncate text-xs text-slate-500">{row.position}</p>
    {row.id && <p className="mt-0.5 font-mono text-[11px] font-semibold text-slate-600" data-testid={`rota-hours-${row.id}`}>{hours.toFixed(1)}h</p>}
  </div>
);

export const RotaGrid = ({ layout, days, rows, shifts, types, isOff, onAdd, onEdit }) => {
  const compact = days.length > 7;
  const hoursFor = (sid) => shifts.filter((s) => s.staff_id === sid).reduce((a, s) => a + s.paid_hours, 0);
  const cellShifts = (row, d) => shifts.filter((s) => (s.staff_id || null) === row.id && s.start.slice(0, 10) === ymd(d));
  const cell = (row, d) => <Cell key={`${row.id}-${ymd(d)}`} day={d} row={row} types={types} compact={compact} off={row.id && isOff(row.id, d)}
    shifts={cellShifts(row, d)} onAdd={onAdd} onEdit={onEdit} />;
  const staffRows = layout !== "staff_cols";
  const colW = compact ? "minmax(96px,1fr)" : "minmax(130px,1fr)";
  const cols = staffRows ? `170px repeat(${days.length}, ${colW})` : `110px repeat(${rows.length}, minmax(140px,1fr))`;
  const minW = staffRows ? 170 + days.length * (compact ? 96 : 130) : 110 + rows.length * 140;
  const printCols = staffRows ? `${compact ? 78 : 120}px repeat(${days.length}, minmax(0,1fr))` : `64px repeat(${rows.length}, minmax(0,1fr))`;
  const dense = compact || rows.length > 8;
  return (
    <div className={`print-grid max-h-[75vh] overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm ${dense ? "print-dense" : ""}`} data-testid={`rota-grid-${staffRows ? "staff-rows" : "staff-cols"}`}>
      <div className="rota-grid-inner grid" style={{ "--cols": cols, "--print-cols": printCols, minWidth: minW }}>
        <div className="sticky left-0 top-0 z-20 bg-white p-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{staffRows ? "Team" : "Date"}</div>
        {staffRows ? days.map((d) => <DayHead key={ymd(d)} d={d} horizontal />) : rows.map((r) => <StaffHead key={r.id || "open"} row={r} hours={hoursFor(r.id)} />)}
        {staffRows
          ? rows.map((row) => (
            <div key={row.id || "open"} className="contents">
              <StaffHead row={row} hours={hoursFor(row.id)} horizontal />
              {days.map((d) => cell(row, d))}
            </div>
          ))
          : days.map((d) => (
            <div key={ymd(d)} className="contents">
              <DayHead d={d} />
              {rows.map((row) => cell(row, d))}
            </div>
          ))}
      </div>
    </div>
  );
};
